import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { reviewsRepoFor } from '../lib/repositories/reviews-repo.js';
import { callSetReviewHidden } from '../lib/callables/reviews.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, cardGridStyle } from '../lib/layout.js';
import type { Review } from '@kilat/shared';

/**
 * /reviews — operator review moderation (feature: reviews).
 *
 * Lists this outlet's post-wash reviews (newest first) so an owner/manager can
 * HIDE an abusive or off-topic one. Hiding flips the review's `hidden` flag via
 * the `setReviewHidden` callable, which re-fires the rating aggregation — the
 * review drops out of the outlet's average, review count, and the public
 * `find` display. Cashiers can view but not moderate (server-enforced; the UI
 * hides the action for them rather than showing a dead-end).
 *
 * No-dead-ends: every state — no tenant, no outlet, loading, error, empty — has
 * an explicit screen with a next action.
 */

/** Bounds read cost — most outlets have far fewer; server pagination ships if one outgrows it. */
const REVIEWS_LOAD_CAP = 300;

export function ReviewsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const { outletId, outlets, loading: outletsLoading } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const roles = claims?.['roles'];
  const role =
    typeof roles === 'object' && roles !== null
      ? (roles as Record<string, string>)[tenantId ?? '']
      : undefined;
  const canModerate = role === 'owner' || role === 'manager';

  const reviewsSub = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = reviewsRepoFor(tenantId, outletId);
    return (cb: (vs: Review[]) => void) =>
      repo.listSnapshotBounded({ orderBy: [['createdAt', 'desc']], limitTo: REVIEWS_LOAD_CAP }, cb);
  }, [tenantId, outletId]);

  const {
    data: reviews,
    loading,
    error,
  } = useTrackedSnapshot<Review[]>(['reviews', tenantId, outletId], reviewsSub);

  if (!tenantId) {
    return (
      <DeadEnd
        title={t.t('dashboard.reviews.noTenantTitle')}
        body={t.t('dashboard.reviews.noTenantBody')}
        cta={{ label: t.t('dashboard.states.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = reviews ?? [];
  const visible = list.filter((r) => !r.hidden);
  const count = visible.length;
  const avg = count > 0 ? visible.reduce((s, r) => s + r.rating, 0) / count : 0;

  return (
    <main
      style={{
        maxWidth: PAGE_FRAME_MAX,
        margin: '0 auto',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      {/* The rating summary is a strip across the top, the average large on
          the right, so the number an owner opens this screen for is read
          before any single review. */}
      <header
        style={{
          marginBottom: 'var(--space-5)',
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface)',
          boxShadow: 'var(--shadow-1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-h1)' }}>
            {t.t('dashboard.reviews.title')}
          </h1>
          <p style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
            {count > 0
              ? t.t('dashboard.reviews.basedOn', { n: count })
              : t.t('dashboard.reviews.subtitle')}
          </p>
        </div>
        {count > 0 ? (
          <div
            role="img"
            aria-label={t.t('dashboard.reviews.summary', { avg: avg.toFixed(1), n: count })}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
              color: 'var(--color-primary)',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-display)',
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {avg.toFixed(1)}
            </span>
            <span style={{ fontSize: 'var(--text-h2)' }}>
              {'★'.repeat(Math.round(avg)) + '☆'.repeat(Math.max(0, 5 - Math.round(avg)))}
            </span>
          </div>
        ) : null}
      </header>

      {!outletId ? (
        outletsLoading ? (
          <StatusLine text={t.t('dashboard.reviews.loading')} />
        ) : outlets.length === 0 ? (
          <EmptyLine
            title={t.t('dashboard.reviews.noOutletTitle')}
            body={t.t('dashboard.reviews.noOutletBody')}
          />
        ) : (
          <EmptyLine
            title={t.t('dashboard.reviews.pickOutletTitle')}
            body={t.t('dashboard.reviews.pickOutletBody')}
          />
        )
      ) : loading ? (
        <StatusLine text={t.t('dashboard.reviews.loading')} />
      ) : error ? (
        <EmptyLine
          tone="error"
          title={t.t('dashboard.reviews.errorTitle')}
          body={t.t('dashboard.reviews.errorBody')}
        />
      ) : list.length === 0 ? (
        <EmptyLine
          title={t.t('dashboard.reviews.emptyTitle')}
          body={t.t('dashboard.reviews.emptyBody')}
        />
      ) : (
        <ul
          style={{
            ...cardGridStyle,
            alignItems: 'stretch',
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {list.map((r) => (
            <ReviewRow
              key={r.id}
              review={r}
              tenantId={tenantId}
              outletId={outletId}
              canModerate={canModerate}
            />
          ))}
        </ul>
      )}
    </main>
  );
}

function ReviewRow({
  review,
  tenantId,
  outletId,
  canModerate,
}: {
  readonly review: Review;
  readonly tenantId: string;
  readonly outletId: string;
  readonly canModerate: boolean;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const who = review.authorName.trim() || t.t('dashboard.reviews.anonymous');
  const when = formatDate(review.createdAt, t.locale);

  async function toggle(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      await callSetReviewHidden({
        tenantId,
        outletId,
        reviewId: review.id,
        hidden: !review.hidden,
      });
      // The live snapshot reflects the flip; nothing else to do.
    } catch (err) {
      logger.error('setReviewHidden failed', { error: String(err) });
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        opacity: review.hidden ? 0.6 : 1,
        display: 'grid',
        // Stars, text, then author + action pinned to the card's foot, so a
        // row of cards in the grid lines its actions up.
        gridTemplateRows: 'auto 1fr auto',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <span
          aria-label={t.t('dashboard.reviews.starsAria', { n: review.rating })}
          style={{
            color: 'var(--color-primary)',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-body)',
          }}
        >
          <span aria-hidden>
            {'★'.repeat(review.rating) + '☆'.repeat(Math.max(0, 5 - review.rating))}
          </span>
        </span>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {when}
        </span>
      </div>

      {review.text ? (
        <p style={{ margin: 0, overflowWrap: 'anywhere' }}>{review.text}</p>
      ) : (
        <p style={{ margin: 0, fontStyle: 'italic', color: 'var(--color-on-surface-muted)' }}>
          {t.t('dashboard.reviews.noComment')}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontWeight: 600,
          }}
        >
          {who}
          {review.hidden ? (
            <span
              style={{
                marginLeft: 'var(--space-2)',
                padding: '0 var(--space-2)',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--color-border)',
                fontWeight: 600,
              }}
            >
              {t.t('dashboard.reviews.hiddenBadge')}
            </span>
          ) : null}
        </span>

        {canModerate ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggle()}
            style={{
              minHeight: 'var(--touch-target)',
              padding: '0 var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-primary)',
              background: 'transparent',
              color: 'var(--color-primary)',
              fontSize: 'var(--text-meta)',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy
              ? t.t('dashboard.reviews.working')
              : review.hidden
                ? t.t('dashboard.reviews.unhide')
                : t.t('dashboard.reviews.hide')}
          </button>
        ) : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {t.t('dashboard.reviews.viewOnly')}
          </span>
        )}
      </div>

      {failed ? (
        <p
          role="alert"
          style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}
        >
          {t.t('dashboard.reviews.actionError')}
        </p>
      ) : null}
    </li>
  );
}

/** Localised absolute date for a moderation timestamp. */
function formatDate(at: { toMillis?: () => number; seconds: number }, locale: string): string {
  const ms = typeof at.toMillis === 'function' ? at.toMillis() : at.seconds * 1000;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(ms));
}

function StatusLine({ text }: { readonly text: string }) {
  return (
    <p role="status" style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
      {text}
    </p>
  );
}

function EmptyLine({
  title,
  body,
  tone = 'muted',
}: {
  readonly title: string;
  readonly body: string;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontWeight: 600,
          color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface)',
        }}
      >
        {title}
      </p>
      <p style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </div>
  );
}

function DeadEnd({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta: { readonly label: string; readonly to: string };
}) {
  return (
    <main
      style={{
        minHeight: '60dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-8)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gap: 'var(--space-3)',
          justifyItems: 'center',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600 }}>{title}</p>
        <p style={{ margin: 0, maxWidth: 360, color: 'var(--color-on-surface-muted)' }}>{body}</p>
        <Link
          to={cta.to}
          style={{
            minHeight: 'var(--touch-target)',
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0 var(--space-5)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {cta.label}
        </Link>
      </div>
    </main>
  );
}
